/* eslint-disable no-param-reassign */
import React, { Component } from "react";
import * as PropTypes from "prop-types";
import { InView } from "react-intersection-observer";
import { get, clamp, isNumber, pick, flatten } from "lodash";
import Term from "../../../../utilities/transcriptLib/Term";
import Word from "./Word";
import S from "../../../../styles/components";
import connectToMedia from "../../../Media/connectToMedia";
import ScrollerContext from "../../../ScrollerContext";
import {
  AgentIcon,
  BotIcon,
  CustomerIcon,
  SystemIcon,
} from "../../../../assets/icons/index";
import moment from "moment";
import equal from "deep-equal";
import connectToScroller from "../connectToScroller";

const TIME_PADDING = 60 * 1000;

/**
 * A margin around the current turn's extend for which .
 * a turn element is considered "current" and will therefor render fully.
 */

function stampToTime(time) {
  if (!(time && isNumber(time))) {
    return time || "00:00";
  }
  const newTime = new Date(time || 0); // still need this in case ms = NaN
  const isHourTimeLabel = newTime.getUTCHours() >= 1;
  return newTime.toISOString().slice(isHourTimeLabel ? 11 : 14, -5);
}

/**
 * a "Turn" represents a line of conversation by a single speaker.
 * Has one or more sentences.
 * Note -- turns do NOT NECESSARILY alternate. In circumstances where
 * a single speaker goes on for some time their sentences may be chunked
 * into sequential turns. (for instance for a single speaker.
 */
class Turn extends Component {
  state = { frameRef: false, inView: false };

  static contextType = ScrollerContext;

  /**
   * whether the currentTime is contained in the turn's start/end range.
   * note - not STRITCLY accurate as some margin (TIME_PADDING) is added to be generous.
   *
   *
   * @param props
   * @returns {boolean}
   */
  containsCurrentTime(props) {
    // even things out of view are fully rendered in a time window.
    if (!props) {
      props = this.props;
    }
    if (!props) return false;

    const { turn, currentTime } = props;
    if (!(isNumber(currentTime) && turn)) {
      return false;
    }

    turn?.freezeRange(); // memoize the turn's time ranges.
    const { start, end } = turn;

    const i =
      clamp(currentTime, start - TIME_PADDING, end + TIME_PADDING) ===
      currentTime;
    return i;
  }

  isFullRender() {
    return get(this, "state.inView") || this.containsCurrentTime();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (!equal(nextProps.media, this.props.media)) return true;
    if (!equal(nextProps.query, this.props.query)) {
      return true;
    }
    return (
      get(this, "state.inView") !== get(nextState, "inView") ||
      this.containsCurrentTime(nextProps) !== this.containsCurrentTime()
    );
  }

  syncFrame(e) {
    if (this.state.frameRef !== e) {
      this.setState({ frameRef: e });
    }
  }

  componentDidMount() {
    const e = get(this.context, "current");
    this.syncFrame(e);
  }

  componentDidUpdate() {
    const e = get(this.context, "current");
    this.syncFrame(e);
  }

  cleanText(text) {
    return text
    // return text.replace(/<\/?[^>]+(>|$)/g, "").trim();
  }

  getTranscriptText(fullRender) {
    const { turn, onSeek, isChatTextMedia } = this.props;
    turn?.freezeWords();
    let phrases;

    // fully formatted word divs
    phrases = turn?.value
      .filter((a) => a)
      .map((sentence) => {
        const sentenceWords = sentence?.value?.filter((t) => t instanceof Term);

        return (
          <S.SentenceMarker key={sentence.id}>
            {sentenceWords?.map((word, idx) => {
              const wordSpaceBuilder = () => {
                // If current "word" is an ending punctuation mark (".", "?", "!"), add trailing space
                const isEndingPunc =
                  word.isMeta &&
                  word.meta === "punc" &&
                  /([?!.])$/.test(word.value);

                if (isEndingPunc) {
                  return `${word.value} `;
                }

                // Check to see if previous "word" is other type of punctuation or open bracket/parenthesis.
                const prevWord = sentenceWords?.[idx - 1];
                const isPrevWordPunc =
                  prevWord && /([#'‘$@[{(/_.?!=-])$/.test(prevWord.value);

                // Check is see if current "word" begins with alphanumeric char
                const isAlphaNumericWord = /(^[a-zA-Z0-9])/.test(word.value);
                const isOpeningPuncWord = /(^[*‘“#&$[({])/.test(word.value);

                return `${
                  ((isAlphaNumericWord && !isPrevWordPunc) ||
                    isOpeningPuncWord) &&
                  idx !== 0
                    ? " "
                    : ""
                }${this.cleanText(word.value)}`;
              };

              return (
                <Word
                  key={word.id}
                  isMeta={word.isMeta}
                  meta={word.meta}
                  value={wordSpaceBuilder()}
                  className={word.getClasses("word")}
                  onClick={() => onSeek({ s: word.start, p: word.p })}
                />
              );
            })}
          </S.SentenceMarker>
        );
      });

    return phrases;
  }

  setSpeakerIcon(turn) {
    switch (
      this.props?.isChatTextMedia
        ? get(turn, "speaker")?.toLowerCase()
        : turn?.get("speaker").toLowerCase()
    ) {
      case "agent":
        return (
          <div className="speaker-icon agent-icon-bk">
            <AgentIcon />
          </div>
        );
      case "bot":
        return (
          <div className="speaker-icon bot-icon-bk">
            <BotIcon />
          </div>
        );
      case "system":
        return (
          <div className="speaker-icon system-icon-bk">
            <SystemIcon />
          </div>
        );
      default:
        return (
          <div className="speaker-icon customer-icon-bk">
            <CustomerIcon />
          </div>
        );
    }
  }

  getMessageTimestamp() {
    // callStartTime is written in ISO8601 format, convert to Unix time
    // in order to add Turn start times (milliseconds)
    // to get a timestamp for each Turn. Timestamps returned in UTC time
    const { turn, isChatTextMedia = false, metadata = {} } = this.props;

    if (!isChatTextMedia) {
      return "";
    }

    const callStartTime =
      metadata?.detail?.callDetails?.callStartTime ||
      metadata?.callDetails?.callStartTime;

    const callStartUnixTime = callStartTime
      ? new Date(callStartTime).getTime()
      : 0;

    return callStartUnixTime
      ? ` — ${moment
          .utc(new Date(callStartUnixTime + (turn?.start || turn?.s)))
          .format("MMM DD, YYYY H:mm:ss A")} UTC`
      : "";
  }

  highlightSelectedKeywordTurn() {
    const { turn = {} } = this.props;
    const hasKeywordQuery = this.props?.query?.some(
      (query) => query.type === "keywords"
    );
    let keywordQueryExistsInTurn = false;
    if (hasKeywordQuery) {
      const queryTimes = flatten(
        this.props?.query
          .filter((query) => query.type === "keywords")
          .map((query) => query.hits.map((hit) => hit.s))
      );
      keywordQueryExistsInTurn = queryTimes.some(
        (query) => query >= turn.s && query <= turn.e
      );
    }
    return keywordQueryExistsInTurn;
  }

  containsText(w, searchedWord, words, index) {
    const searchedWordArr = searchedWord
      .trim()
      .replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, "")
      .split(" ");
    if (searchedWordArr.length > 1) {
      let firstMatch = searchedWordArr.shift();
      if (w.toLowerCase().includes(firstMatch.toLowerCase())) {
        let nextString = [];
        for (let i = 1; i <= searchedWordArr.length; i++) {
          if (words[index + i]) {
            nextString.push(
              words[index + i]
                .replace(/<\/?[^>]+(>|$)/g, "")
                .replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, "")
                .toLowerCase()
            );
          }
        }
        return (
          searchedWordArr.length === nextString.length &&
          searchedWordArr.join(" ").toLowerCase().trim() ===
            nextString.join(" ")
        );
      }
    }

    return (
      w.replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, "").toLowerCase() ===
      searchedWord
        .replace(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g, "")
        .trim()
        .toLowerCase()
    );
  }

  highlightSearchedWordInTurn() {
    const { turn = {} } = this.props;
    const hasSearchKeyword = this.props?.query?.some(
      (query) => query.type === "search"
    );
    if (hasSearchKeyword) {
      const queryTimes = this.props?.query
        .filter((query) => query.type === "search")
        .map((query) => ({
          label: query.label,
          hit: query.hits,
        }));

        
      if (queryTimes.length && queryTimes.every((val) => val.hit.length > 0)) {
        const words = this.cleanText(turn.text).split(" ");

        queryTimes.forEach((query) => {
          const searchedWord = query.label;
          const regex = new RegExp(`${searchedWord}`, "g");

          let matches = [...turn.text.matchAll(regex)];
          let hitIndex = 0;
          let hit = query.hit.filter(
            (hit) => hit.s >= turn.s && hit.s <= turn.e
          );

          if (!hit.length) {
            query.hit.filter(
              (val) =>
                val.p ===
                parseInt(document.getElementById("chat-frame-value").value)
            );
          }

          words.forEach((w, i) => {
            if (
              hit.length &&
              this.containsText(w, searchedWord, words, i) &&
              hit[hitIndex]?.p
            ) {
              if (i - 1 >= 0 && words[i - 1]) {
                words[i - 1] = `<span class='word-index-${
                  hit[hitIndex]?.p - 1
                }'>${words[i - 1]}</span>`;
              }
              if (i - 2 >= 0 && words[i - 2]) {
                words[i - 2] = `<span class='word-index-${
                  hit[hitIndex]?.p - 2
                }'>${words[i - 2]}</span>`;
              }

              const searchedWordArr = searchedWord.trim().split(" ");
              for (let j = 1; j <= searchedWordArr.length + 2; j++) {
                if (i + j <= words.length - 1 && words[i + j]) {
                  words[i + j] = `<span class='word-index-${
                    hit[hitIndex]?.p + j
                  }'>${words[i + j]}</span>`;
                }
              }

              words[
                i
              ] = `<span class=' word-index-${hit[hitIndex]?.p}'>${w}</span>`;
              hitIndex += 1;
            }
          });
        });

        return words.join(" ");
      }
    }
    return this.cleanText(turn.text);
  }

  render() {
    const {
      turn = {},
      speakerColors,
      onSeek,
      isChatTextMedia,
      media,
      query,
      turnIndex,
      currentTime,
      currentPosition,
    } = this.props;

    const ref = get(this, "context.scrollRef");
    const fullRender = this.isFullRender();

    const turnContentContainer = document.getElementById(
      `chat-turn-content-${turnIndex}`
    );

    if (turnContentContainer) {
      turnContentContainer.innerHTML = this.highlightSearchedWordInTurn();
    }

    return (
      <InView
        as="div"
        ref={ref}
        onChange={(inView) => {
          if (get(this, "state.inView") !== inView) {
            this.setState({ inView });
          }
        }}
      >
        <S.TranscriptTurn
          className={`${
            isChatTextMedia
              ? this.highlightSelectedKeywordTurn()
                ? "chat-turn-range-true"
                : ""
              : turn?.getClasses("turn")
          } ${
            isChatTextMedia &&
            media?.currentTime >= turn.s &&
            media?.currentTime <= turn.e
              ? "chat-turn-time-scroll-true"
              : ""
          }`}
        >
          <S.TurnBody>
            <S.TurnLabels style={{ display: "flex", alignItems: "Center" }}>
              <S.TurnSpeakerIcon>{this.setSpeakerIcon(turn)}</S.TurnSpeakerIcon>
              <div className="turn-labels-speaker-time">
                <S.TurnSpeaker
                  // color={speakerColors.get(turn.get('speaker'))}
                  color="#262D38"
                >
                  {isChatTextMedia
                    ? get(turn, "speaker") || get(turn, "speakerName")
                    : turn?.get("speaker")}
                </S.TurnSpeaker>
                <S.TurnTime>
                  <S.TimeLink
                    onClick={() =>
                      onSeek({ s: turn?.start, p: turn?._words[0].p })
                    }
                    style={{ color: "rgba(38, 45, 56, 0.5)" }}
                  >
                    {turn?.createTime ? (
                      <>{`${moment
                        .utc(new Date(turn.createTime))
                        .format("MMM DD, YYYY H:mm:ss A")} UTC`}</>
                    ) : (
                      <>
                        {stampToTime(turn?.start || turn?.s)}
                        {this.getMessageTimestamp()}
                      </>
                    )}
                  </S.TimeLink>
                </S.TurnTime>
              </div>
            </S.TurnLabels>
          </S.TurnBody>
          <S.TurnBody>
            <div>
              {isChatTextMedia ? (
                <S.SentenceMarker
                  id={`chat-turn-content-${turnIndex}`}
                  className={`${
                    this.highlightSelectedKeywordTurn()
                      ? "chat-turn-highlighted"
                      : ""
                  } `}
                >
                  {/* {this.highlightSearchedWordInTurn()} */}
                  {/* {turn.text.split(" ").map((word, index) => (
                    <span className={`chat-word-index-${index}`}>{word} </span>
                  ))} */}
                </S.SentenceMarker>
              ) : (
                this.getTranscriptText(fullRender)
              )}
            </div>
          </S.TurnBody>
        </S.TranscriptTurn>
      </InView>
    );
  }
}

Turn.propTypes = {
  turn: PropTypes.any,
  currentTime: PropTypes.number,
  speakers: PropTypes.array,
  speakerColors: PropTypes.any,
  onSeek: PropTypes.func,
};

export default connectToMedia((state) =>
  pick(state, ["media", "currentTime", "currentPosition", "query"])
)(Turn);
